/* Mission Control: shared behavior.
   Reveals, numeral lock-in, header state, mobile nav, click-to-play embeds, rails. */
(() => {
  const root = document.documentElement;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const design = root.classList.contains('shopify-design-mode');

  /* ----- Acquisition of signal ----- */
  function lockNumerals(el) {
    // Text content is final in the DOM from the start; the animation only counts up to it.
    const target = el.dataset.value || el.textContent;
    if (reduced || !/\d/.test(target) || el.__locked) return;
    el.__locked = 1;
    const delay = parseInt(el.dataset.lockDelay || '0', 10);
    if (delay && !el.__waited) {
      el.__waited = 1;
      el.__locked = 0;
      setTimeout(() => lockNumerals(el), delay);
      return;
    }
    // Count up to the figure, never past it: no frame may show a number larger than the claim.
    const m = target.match(/\d[\d,]*(\.\d+)?/);
    const t0 = performance.now();
    const dur = 1100;
    el.setAttribute('aria-label', target);
    if (!m) return;
    const raw = m[0];
    const value = parseFloat(raw.replace(/,/g, ''));
    const decimals = (raw.split('.')[1] || '').length;
    const grouped = raw.includes(',');
    const fmt = (n) => {
      const fixed = n.toFixed(decimals);
      return grouped ? Number(fixed).toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }) : fixed;
    };
    // Single-digit figures (5M+, 7-Figure) would pass through misleading values, so they don't count
    if (value < 10 || /^[<~≈]/.test(target.trim())) return;
    const start = 0;
    function frame(now) {
      const p = Math.min(1, (now - t0) / dur);
      const e = 1 - Math.pow(1 - p, 3);
      const n = Math.min(value, start + (value - start) * e);
      el.textContent = target.replace(raw, fmt(decimals ? n : Math.floor(n)));
      if (p < 1) requestAnimationFrame(frame);
      else el.textContent = target;
    }
    requestAnimationFrame(frame);
  }

  function initReveal(scope) {
    const els = scope.querySelectorAll('[data-reveal], [data-lock]');
    if (!('IntersectionObserver' in window) || reduced || design) {
      els.forEach((el) => el.classList.add('is-in'));
      return;
    }
    root.classList.add('mc-armed');
    // A fully clipped wipe has no visible area, so it is watched through its parent.
    const proxies = new Map();
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          const targets = proxies.get(e.target) || [e.target];
          targets.forEach((t) => {
            t.classList.add('is-in');
            if (t.hasAttribute('data-lock')) lockNumerals(t);
            t.querySelectorAll('[data-lock]').forEach(lockNumerals);
          });
          io.unobserve(e.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0 }
    );
    els.forEach((el) => {
      if (el.hasAttribute('data-lock') && el.closest('[data-reveal]')) return;
      if (el.dataset.reveal === 'wipe' && el.parentElement) {
        const host = el.parentElement;
        if (!proxies.has(host)) { proxies.set(host, []); io.observe(host); }
        proxies.get(host).push(el);
        return;
      }
      io.observe(el);
    });
  }

  /* ----- Header ----- */
  function initHeader() {
    const header = document.querySelector('[data-mc-header]');
    if (!header) return;
    const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });

    const toggle = header.querySelector('[data-mc-menu-toggle]');
    const panel = document.getElementById(toggle?.getAttribute('aria-controls'));
    if (!toggle || !panel) return;
    const close = () => {
      toggle.setAttribute('aria-expanded', 'false');
      panel.hidden = true;
      document.body.style.overflow = '';
      header.classList.remove('is-open');
    };
    toggle.addEventListener('click', () => {
      const open = toggle.getAttribute('aria-expanded') !== 'true';
      if (!open) return close();
      toggle.setAttribute('aria-expanded', 'true');
      panel.hidden = false;
      header.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      panel.querySelector('a')?.focus({ preventScroll: true });
    });
    panel.addEventListener('click', (e) => { if (e.target.closest('a')) close(); });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && toggle.getAttribute('aria-expanded') === 'true') { close(); toggle.focus(); }
    });
    window.matchMedia('(min-width: 990px)').addEventListener('change', (m) => m.matches && close());
  }

  /* ----- Click-to-play embeds: poster first, player only on intent ----- */
  function embedSrc(url) {
    try { url = decodeURIComponent(url); } catch (e) {}
    let m = url.match(/tiktok\.com\/@[^/]+\/video\/(\d+)/) || url.match(/tiktok\.com\/(?:player\/v1|embed\/v2)\/(\d+)/);
    if (m) return { src: `https://www.tiktok.com/player/v1/${m[1]}?autoplay=1&music_info=1&description=1&rel=0&native_context_menu=0`, kind: 'tiktok' };
    m = url.match(/instagram\.com\/(?:reel|p)\/([\w-]+)/);
    if (m) return { src: `https://www.instagram.com/reel/${m[1]}/embed/`, kind: 'instagram' };
    m = url.match(/(?:youtu\.be\/|youtube(?:-nocookie)?\.com\/(?:shorts\/|embed\/|live\/|v\/))([\w-]{11})/) || (/youtube\.com\/watch/.test(url) && url.match(/[?&]v=([\w-]{11})/));
    if (m) return { src: `https://www.youtube-nocookie.com/embed/${m[1]}?autoplay=1&rel=0`, kind: 'youtube' };
    // LinkedIn's official post embed (a post that carries a native video plays inside it)
    m = url.match(/linkedin\.com\/embed\/feed\/update\/(urn:li:(?:ugcPost|share|activity):\d+)/);
    if (m) return { src: `https://www.linkedin.com/embed/feed/update/${m[1]}`, kind: 'linkedin' };
    return null;
  }

  function initEmbeds(scope) {
    scope.querySelectorAll('[data-mc-embed]').forEach((btn) => {
      if (btn.__mc) return;
      btn.__mc = 1;
      const info = embedSrc(btn.dataset.mcEmbed || '');
      if (!info) { btn.hidden = true; return; }
      btn.addEventListener('click', () => {
        const frame = btn.closest('[data-mc-screen]');
        const iframe = document.createElement('iframe');
        iframe.src = info.src;
        iframe.title = btn.getAttribute('aria-label') || 'Video player';
        iframe.allow = 'autoplay; encrypted-media; fullscreen; picture-in-picture';
        iframe.allowFullscreen = true;
        iframe.loading = 'eager';
        iframe.className = 'mc-screen__player';
        frame.classList.add('is-playing', `is-${info.kind}`);
        const loading = document.createElement('span');
        loading.className = 'mc-screen__loading';
        loading.setAttribute('aria-hidden', 'true');
        loading.innerHTML = '<i></i>LOADING';
        frame.appendChild(loading);
        iframe.addEventListener('load', () => loading.remove(), { once: true });
        setTimeout(() => loading.remove(), 8000);
        frame.appendChild(iframe);
        iframe.focus();
      });
    });
  }

  /* ----- Rails: scroll-snap strips with console step buttons ----- */
  function initRails(scope) {
    scope.querySelectorAll('[data-mc-rail]').forEach((rail) => {
      if (rail.__mc) return;
      rail.__mc = 1;
      const track = rail.querySelector('[data-mc-track]');
      const prev = rail.querySelector('[data-mc-prev]');
      const next = rail.querySelector('[data-mc-next]');
      const readout = rail.querySelector('[data-mc-position]');
      const items = [...track.children];
      const step = () => (items[0]?.getBoundingClientRect().width || 300) + parseFloat(getComputedStyle(track).columnGap || 0);
      const update = () => {
        const max = track.scrollWidth - track.clientWidth - 2;
        if (prev) prev.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= max;
        if (readout) {
          const idx = Math.min(items.length, Math.round(track.scrollLeft / step()) + 1);
          readout.textContent = `${String(idx).padStart(2, '0')} / ${String(items.length).padStart(2, '0')}`;
        }
      };
      prev?.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: reduced ? 'auto' : 'smooth' }));
      next?.addEventListener('click', () => track.scrollBy({ left: step(), behavior: reduced ? 'auto' : 'smooth' }));
      track.addEventListener('scroll', () => requestAnimationFrame(update), { passive: true });
      window.addEventListener('resize', update);
      update();
    });
  }

  /* ----- Ascent trajectory: path draws itself with scroll ----- */
  function initAscent(scope) {
    scope.querySelectorAll('[data-mc-ascent]').forEach((el) => {
      if (el.__mc) return;
      el.__mc = 1;
      const path = el.querySelector('[data-mc-ascent-path]');
      const nodes = [...el.querySelectorAll('[data-mc-node]')];
      if (!path) return;
      const len = path.getTotalLength();
      path.style.strokeDasharray = `${len}`;
      const set = (p) => {
        path.style.strokeDashoffset = `${len * (1 - p)}`;
        nodes.forEach((n, i) => n.classList.toggle('is-lit', p >= (i + 0.35) / nodes.length));
      };
      if (reduced || design) { set(1); return; }
      // The path follows scroll only while the plot is near the screen. Its document position is cached
      // (re-measured on resize), so the scroll work never forces a layout; writes happen only on change.
      let top = 0, height = 1, near = false, lastP = -1, ticking = false;
      const measure = () => { const r = el.getBoundingClientRect(); top = r.top + window.scrollY; height = r.height; };
      const update = () => {
        ticking = false;
        const vh = window.innerHeight;
        const p = Math.min(1, Math.max(0, (vh * 0.85 - (top - window.scrollY)) / (height * 0.9 + vh * 0.25)));
        const q = Math.round(p * 400) / 400;
        if (q !== lastP) { lastP = q; set(q); }
      };
      const onScroll = () => { if (near && !ticking) { ticking = true; requestAnimationFrame(update); } };
      measure();
      update();
      new ResizeObserver(() => { measure(); update(); }).observe(el);
      window.addEventListener('resize', () => { measure(); update(); });
      new IntersectionObserver((entries) => { near = entries[entries.length - 1].isIntersecting; measure(); update(); }, { rootMargin: '25% 0px' }).observe(el);
      window.addEventListener('scroll', onScroll, { passive: true });
    });
  }

  /* ----- Brand marks: record each file's aspect so CSS can equalise optical area ----- */
  function initLogos(scope) {
    scope.querySelectorAll('img[data-mc-logo]').forEach((img) => {
      const set = () => {
        if (img.naturalWidth && img.naturalHeight) img.style.setProperty('--a', Math.min(9, Math.max(0.8, img.naturalWidth / img.naturalHeight)).toFixed(3));
      };
      if (img.complete) set(); else img.addEventListener('load', set, { once: true });
    });
  }

  /* ----- Hero scene: write scroll progress (0 → 1) across the pinned section ----- */
  function initHeroStory(scope) {
    // Desktop only: phones and tablets get a stacked, unpinned hero
    const pinned = window.matchMedia('(min-width: 990px)');
    scope.querySelectorAll('[data-mc-hero]').forEach((hero) => {
      if (hero.__mc || reduced) return;
      hero.__mc = 1;
      let ticking = false;
      const update = () => {
        ticking = false;
        if (!pinned.matches) { hero.style.setProperty('--p', '0'); return; }
        const r = hero.getBoundingClientRect();
        const run = Math.max(1, r.height - window.innerHeight);
        const p = Math.min(1, Math.max(0, -r.top / run));
        hero.style.setProperty('--p', p.toFixed(4));
      };
      window.addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } }, { passive: true });
      window.addEventListener('resize', update);
      update();
    });
  }

  /* ----- Scroll-lit prose: wrap words so each one lights as it crosses the viewport ----- */
  function initLit(scope) {
    if (reduced || design || !CSS.supports('animation-timeline: view()')) return;
    scope.querySelectorAll('.mc-lit').forEach((el) => {
      if (el.__mc) return;
      el.__mc = 1;
      const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
      const nodes = [];
      while (walker.nextNode()) if (walker.currentNode.nodeValue.trim()) nodes.push(walker.currentNode);
      nodes.forEach((node) => {
        const frag = document.createDocumentFragment();
        node.nodeValue.split(/(\s+)/).forEach((part) => {
          if (!part) return;
          if (/^\s+$/.test(part)) { frag.appendChild(document.createTextNode(part)); return; }
          const w = document.createElement('span');
          w.className = 'mc-lit__w';
          w.textContent = part;
          frag.appendChild(w);
        });
        node.parentNode.replaceChild(frag, node);
      });
    });
  }

  /* ----- Pixel resolve: logos arrive as coarse mosaics of themselves and sharpen in 8-bit steps ----- */
  function initPixelResolve(scope) {
    if (reduced || design || !('IntersectionObserver' in window)) return;
    scope.querySelectorAll('[data-mc-pixelate]').forEach((list) => {
      if (list.__mcpx) return;
      list.__mcpx = 1;
      const cells = [...list.children];
      list.classList.add('is-px-armed');
      const tmp = document.createElement('canvas');
      const tctx = tmp.getContext('2d');
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      const resolve = (cell, i) => {
        const img = cell.querySelector('img.mc-logo');
        if (!img) { cell.classList.add('is-resolved'); return; }
        const go = () => {
          const w = img.offsetWidth, h = img.offsetHeight;
          if (!w || !h || !img.naturalWidth) { cell.classList.add('is-resolved'); return; }
          const cv = document.createElement('canvas');
          cv.className = 'mc-px-canvas';
          cv.width = Math.round(w * dpr); cv.height = Math.round(h * dpr);
          Object.assign(cv.style, { left: `${img.offsetLeft}px`, top: `${img.offsetTop}px`, width: `${w}px`, height: `${h}px`, filter: getComputedStyle(img).filter, opacity: getComputedStyle(img).opacity });
          cell.appendChild(cv);
          const c = cv.getContext('2d');
          const draw = (block) => {
            const sw = Math.max(2, Math.round(w / block)), sh = Math.max(2, Math.round(h / block));
            tmp.width = sw; tmp.height = sh;
            tctx.imageSmoothingEnabled = true; tctx.clearRect(0, 0, sw, sh); tctx.drawImage(img, 0, 0, sw, sh);
            c.imageSmoothingEnabled = false; c.clearRect(0, 0, cv.width, cv.height); c.drawImage(tmp, 0, 0, sw, sh, 0, 0, cv.width, cv.height);
          };
          [12, 7, 4].forEach((b, k) => setTimeout(() => draw(b), k * 110));
          setTimeout(() => { cell.classList.add('is-resolved'); cv.remove(); }, 3 * 110);
        };
        setTimeout(() => (img.complete ? go() : img.addEventListener('load', go, { once: true })), i * 55);
      };
      const io = new IntersectionObserver((entries) => {
        if (!entries[0].isIntersecting) return;
        io.disconnect();
        cells.forEach(resolve);
      }, { rootMargin: '0px 0px -8% 0px' });
      io.observe(list);
    });
  }

  /* ----- Off-screen sections rest: their looping CSS animations pause until they come back near the screen ----- */
  let restIO;
  function initRest(scope) {
    if (!('IntersectionObserver' in window)) return;
    restIO = restIO || new IntersectionObserver((entries) => {
      entries.forEach((e) => e.target.classList.toggle('mc-offscreen', !e.isIntersecting));
    }, { rootMargin: '200px 0px' });
    const secs = scope.matches?.('.shopify-section') ? [scope] : scope.querySelectorAll('#MainContent > .shopify-section, .shopify-section-group-footer-group');
    secs.forEach((s) => { if (!s.__mcRest) { s.__mcRest = 1; restIO.observe(s); } });
  }

  /* ----- 16-bit mode (a secret, Theme settings, Effects): the classic keyboard code, or seven taps on the footer
     lantern. Its look and code (mc-16bit.css / mc-16bit.js) load only when someone finds it. ----- */
  function initBit16() {
    const cfg = window.McBit16;
    if (!cfg || design || !(root.getAttribute('data-fx') || '').split(' ').includes('bit16')) return;
    let loading = null;
    const load = () => loading || (loading = new Promise((ok, fail) => {
      let n = 0;
      const one = () => { if (++n === 2) ok(); };
      const css = document.createElement('link');
      css.rel = 'stylesheet';
      css.href = cfg.css;
      css.onload = one;
      css.onerror = fail;
      document.head.appendChild(css);
      const js = document.createElement('script');
      js.src = cfg.js;
      js.onload = one;
      js.onerror = fail;
      document.head.appendChild(js);
    }).catch(() => { loading = null; }));
    const toggle = (via) => load().then(() => window.Mc16 && window.Mc16.toggle(via));
    const SEQ = ['arrowup', 'arrowup', 'arrowdown', 'arrowdown', 'arrowleft', 'arrowright', 'arrowleft', 'arrowright', 'b', 'a'];
    let at = 0, startY = 0;
    document.addEventListener('keydown', (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey || e.repeat) return;
      if (e.target.closest && e.target.closest('input, textarea, select, [contenteditable]')) return;
      const k = (e.key || '').toLowerCase();
      if (k === SEQ[at]) {
        if (at === 0) startY = window.scrollY;
        at++;
        if (at === 4) load();
        // the code's arrow keys scroll the page as they go: it lands back where the visitor started
        if (at === SEQ.length) {
          at = 0;
          const back = () => window.scrollTo({ top: startY, behavior: 'instant' });
          back();
          setTimeout(back, 320); // again once the keys' smooth scrolling has run (behind the wipe)
          toggle('keys');
        }
      } else if (k === 'arrowup') { if (at !== 2) { at = 1; startY = window.scrollY; } } else at = 0;
    });
    let taps = 0, last = 0;
    document.addEventListener('click', (e) => {
      const lamp = e.target.closest && e.target.closest('[data-mc-lamp]');
      if (!lamp) return;
      const now = performance.now();
      taps = now - last < 1500 ? taps + 1 : 1;
      last = now;
      lamp.classList.remove('is-tap');
      void lamp.offsetWidth;
      lamp.classList.add('is-tap');
      if (taps === 3) load();
      if (taps >= 7) { taps = 0; toggle('lamp'); }
    });
  }

  function init(scope = document) {
    initRest(scope);
    initHeroStory(scope);
    initLit(scope);
    initLogos(scope);
    initReveal(scope);
    initPixelResolve(scope);
    initEmbeds(scope);
    initRails(scope);
    initAscent(scope);
  }

  initHeader();
  initBit16();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
